import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfShimmer } from "@/lib/emotionTw";

export type SkeletonVariant = "text" | "block" | "card" | "avatar";

export type SkeletonProps = HTMLAttributes<HTMLDivElement> & {
  variant?: SkeletonVariant | null;
  animated?: boolean;
};

/* Exact compiled output of the pre-migration Tailwind utilities. The shimmer
 * gradient's empty `--tw-gradient-*-position` variables are substituted away
 * so the rule stays self-contained. */

const skeletonVariantStyles: Record<SkeletonVariant, CSSObject> = {
  text: { height: "1rem", borderRadius: "var(--bfrs-radius-pill)" },
  block: { height: "6rem", borderRadius: "var(--bfrs-radius-lg)" },
  card: { height: "10rem", borderRadius: "var(--bfrs-radius-xl)" },
  avatar: { width: "2.5rem", height: "2.5rem", borderRadius: "var(--bfrs-radius-pill)" }
};

/* `content: var(--tw-content)` intentionally relies on the retained
 * `:where(.bfrs) *::before { --tw-content: "" }` global reset — exactly like
 * the compiled utilities did. Outside a .bfrs scope the shimmer ::before does
 * not materialize, matching the pre-migration behaviour. */
const shimmerBase: CSSObject = {
  content: "var(--tw-content)",
  position: "absolute",
  top: 0,
  bottom: 0,
  width: "100%",
  backgroundImage: "linear-gradient(to right,transparent,hsl(var(--bfrs-color-surface-card) / .7),transparent)"
};

const SkeletonRoot = styled("div", {
  label: "AgenticSkeleton-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: SkeletonVariant; animated: boolean } }>(({ ownerState }) => [
  {
    position: "relative",
    overflow: "hidden",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-raised) / var(--tw-bg-opacity, 1))"
  },
  skeletonVariantStyles[ownerState.variant],
  ownerState.animated
    ? {
        "&:before": {
          ...shimmerBase,
          left: "-100%",
          ...kfShimmer,
          animation: "bfrs-bfrs-shimmer 1.5s var(--bfrs-ease-standard) infinite"
        }
      }
    : {
        "&:before": {
          ...shimmerBase,
          left: 0
        }
      }
]);

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(({ animated = true, className, variant, ...props }, ref) => (
  <SkeletonRoot
    ref={ref}
    aria-hidden="true"
    ownerState={{ variant: variant ?? "block", animated }}
    className={className}
    {...props}
  />
));

Skeleton.displayName = "Skeleton";
